import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';
import { Plus, FileText, Phone, Mail, Users, Trash2, MessageSquare } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import { Dialog, Field, Tabs, EmptyState, ErrorState, TableSkeleton, PersonAvatar, fmtDate } from '../../components/ui';
import StaffHeader from './StaffHeader';
import { useStaffRecord, useStableCallback, IconButton } from './hrKit';

const TYPES = {
  note: { label: 'Note', icon: FileText },
  call: { label: 'Call', icon: Phone },
  email: { label: 'Email', icon: Mail },
  meeting: { label: 'Meeting', icon: Users },
};
const localDayKey = (iso) => { const d = new Date(iso); if (Number.isNaN(d.getTime())) return ''; return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const timeOf = (iso) => { const d = new Date(iso); return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' }).toLowerCase(); };

function AddDialog({ open, staffId, onSaved, onClose }) {
  const [form, setForm] = useState({ type: 'note', subject: '', body: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setForm({ type: 'note', subject: '', body: '' }); setError(''); } }, [open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const close = useStableCallback(() => { if (!saving) onClose(); });

  async function save(e) {
    e.preventDefault();
    if (!form.body.trim()) { setError('Write what was said or agreed.'); return; }
    setSaving(true);
    try {
      await api.post(`/staff/${staffId}/communications`, form);
      toast.success(`${TYPES[form.type].label} logged`);
      setSaving(false); onSaved(); onClose();
    } catch (err) { setError(err.response?.data?.message || "It wasn't saved. Try again."); setSaving(false); }
  }

  return (
    <Dialog open={open} onClose={close} size="md" title="Log a communication"
      footer={(
        <>
          <button type="button" onClick={close} className="btn-secondary" disabled={saving}>Cancel</button>
          <button type="submit" form="comm-form" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </>
      )}>
      <form id="comm-form" onSubmit={save} className="space-y-4">
        <fieldset>
          <legend className="mb-1.5 text-[13px] font-medium text-slate-700">Type</legend>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {Object.entries(TYPES).map(([k, t]) => {
              const on = form.type === k; const Icon = t.icon;
              return (
                <button key={k} type="button" aria-pressed={on} onClick={() => set('type', k)}
                  className={clsx('flex h-10 items-center justify-center gap-1.5 rounded-lg border text-[13px] font-medium transition-colors duration-150',
                    on ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-slate-200 text-slate-600 hover:border-slate-300')}>
                  <Icon size={14} aria-hidden="true" />{t.label}
                </button>
              );
            })}
          </div>
        </fieldset>
        <Field label="Subject">{(id) => <input id={id} className="input-field" value={form.subject} onChange={(e) => set('subject', e.target.value)} />}</Field>
        <Field label="Details" required error={error}>{(id) => <textarea id={id} className="input-field" rows={5} value={form.body} onChange={(e) => { set('body', e.target.value); setError(''); }} />}</Field>
      </form>
    </Dialog>
  );
}

export default function StaffCommunications() {
  const { id } = useParams();
  const confirm = useConfirm();
  const { staff, error: staffError, reload: reloadStaff } = useStaffRecord(id);
  const [comms, setComms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [filter, setFilter] = useState('all');

  const load = useCallback(() => {
    setLoading(true); setLoadError(null);
    api.get(`/staff/${id}/communications`)
      .then((r) => setComms(Array.isArray(r.data) ? r.data : []))
      .catch((e) => setLoadError(e.response?.data?.message || "We couldn't load communications. Try again."))
      .finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  async function deleteComm(c) {
    if (!await confirm(`This ${(TYPES[c.type] || TYPES.note).label.toLowerCase()} will be deleted from the record. This can't be undone.`, { title: 'Delete communication?', confirmLabel: 'Delete', danger: true })) return;
    try {
      await api.delete(`/staff/${id}/communications/${c.id}`);
      toast.success('Deleted');
      setComms((list) => list.filter((x) => x.id !== c.id));
    } catch (e) { toast.error(e.response?.data?.message || "It wasn't deleted. Try again."); }
  }

  const counts = useMemo(() => {
    const n = { all: comms.length };
    Object.keys(TYPES).forEach((k) => { n[k] = comms.filter((c) => (c.type || 'note') === k).length; });
    return n;
  }, [comms]);
  const grouped = useMemo(() => {
    const list = filter === 'all' ? comms : comms.filter((c) => (c.type || 'note') === filter);
    const g = {};
    list.forEach((c) => { const k = localDayKey(c.created_at); (g[k] = g[k] || []).push(c); });
    return Object.entries(g).sort(([a], [b]) => b.localeCompare(a));
  }, [comms, filter]);
  const box = (child) => <div className="rounded-xl border border-slate-200 bg-white">{child}</div>;

  return (
    <div>
      <StaffHeader staff={staff} error={staffError} onRetry={reloadStaff} />
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="max-w-full overflow-x-auto">
            <Tabs variant="segmented" size="sm" ariaLabel="Communication type" value={filter} onChange={setFilter}
              tabs={[{ id: 'all', label: 'All', count: counts.all }, ...Object.entries(TYPES).map(([k, t]) => ({ id: k, label: `${t.label}s`, count: counts[k] }))]} />
          </div>
          <button type="button" onClick={() => setAddOpen(true)} className="btn-primary btn-sm ml-auto"><Plus size={14} aria-hidden="true" />Log communication</button>
        </div>

        {loading ? box(<TableSkeleton rows={3} cols={2} />)
          : loadError ? box(<ErrorState message={loadError} onRetry={load} />)
            : grouped.length === 0 ? box(
              comms.length === 0
                ? <EmptyState icon={MessageSquare} title="Nothing logged yet" hint="Log calls, emails and meetings with this worker so everyone can see what was agreed." action={<button type="button" onClick={() => setAddOpen(true)} className="btn-secondary btn-sm"><Plus size={14} aria-hidden="true" />Log the first one</button>} />
                : <EmptyState icon={MessageSquare} title={`No ${TYPES[filter].label.toLowerCase()}s logged`} action={<button type="button" onClick={() => setFilter('all')} className="btn-secondary btn-sm">Show all</button>} />,
            ) : (
              <div className="space-y-6">
                {grouped.map(([day, list]) => (
                  <section key={day} aria-label={day ? fmtDate(day) : 'Undated'}>
                    <h3 className="mb-2 text-[12.5px] font-semibold text-slate-500">{day ? fmtDate(day) : 'Undated'}</h3>
                    <ul className="space-y-2">
                      {list.map((c) => {
                        const t = TYPES[c.type] || TYPES.note; const Icon = t.icon;
                        return (
                          <li key={c.id} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4">
                            <PersonAvatar id={c.created_by} name={c.created_by_name || 'Staff'} size="sm" />
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <span className="text-sm font-semibold text-slate-900">{c.created_by_name || 'Staff'}</span>
                                <span className="badge-gray"><Icon size={11} aria-hidden="true" />{t.label}</span>
                                <span className="text-[12.5px] text-slate-500">{timeOf(c.created_at)}</span>
                              </div>
                              {c.subject && <p className="mt-1 text-sm font-medium text-slate-800">{c.subject}</p>}
                              <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{c.body}</p>
                            </div>
                            <IconButton label="Delete" icon={Trash2} danger onClick={() => deleteComm(c)} />
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                ))}
              </div>
            )}
      </div>
      <AddDialog open={addOpen} staffId={id} onSaved={load} onClose={() => setAddOpen(false)} />
    </div>
  );
}
